<template>
    <div class="switch-plan-modal">
        <b-modal ref="switchPlanModal" hide-footer size="lg" centered>
            <div class="row g-4">

                <!-- Current Subscription -->
                <div class="col-12">
                    <div class="p-3 border rounded-3 d-flex justify-content-between">
                        <div>
                            <p class="mb-1 fs-14 text-muted">Current Subscription</p>
                            <h5 class="fw-bold mb-0">{{ userCurrentSubscriptionName }}</h5>
                        </div>
                        <div v-if="userCurrentPlanInterval">
                            <p class="mb-1 fs-14 text-muted">Current Interval</p>
                            <h5 class="fw-bold mb-0">{{ userCurrentPlanInterval }}</h5>
                        </div>
                    </div>
                </div>

                <div class="col-12 mt-4">
                    <p class="fs-18 fw-bold">Choose Billing Cycle</p>

                    <div class="row g-3">
                        <div class="col-md-6">
                            <label class="w-100 border rounded-3 billing-card p-3 position-relative" :class="{
                                active: form.interval === 'month',
                                disabled: isActiveSubscription && currentIntervalRaw === 'month'
                            }">
                                <span v-if="isActiveSubscription && currentIntervalRaw === 'month'"
                                    class="current-plan-badge">
                                    Current Plan
                                </span>
                                <input type="radio" value="month" class="d-none" @change="manageBillingType('month')" />

                                <div class="d-flex justify-content-between align-items-start">
                                    <div>
                                        <h6 class="fw-bold mb-1">Monthly Plan</h6>
                                        <p class="fs-13 mb-1 opacity-75">$9.00 / month</p>
                                    </div>
                                    <h5 class="fw-bold mb-0">$9.00</h5>
                                </div>
                            </label>
                        </div>

                        <div class="col-md-6">
                            <label class="w-100 border rounded-3 billing-card p-3 position-relative"
                                :class="{ active: form.interval === 'year',
                                    disabled: isActiveSubscription && currentIntervalRaw === 'year'
                                 }">
                                <span v-if="isActiveSubscription && currentIntervalRaw === 'year'"
                                    class="current-plan-badge">
                                    Current Plan
                                </span>
                                <input type="radio" value="year" class="d-none" @change="manageBillingType('year')" />

                                <div class="d-flex justify-content-between align-items-start">
                                    <div>
                                        <h6 class="fw-bold mb-1">Yearly Plan</h6>
                                        <p class="fs-13 mb-1 opacity-75">$99.00 / year</p>
                                    </div>
                                    <h5 class="fw-bold mb-0">
                                        ${{ form.interval === 'year' ? finalPrice.toFixed(2) : '99.00' }}
                                    </h5>
                                </div>
                            </label>
                        </div>
                    </div>
                </div>

                <div class="col-12 mt-4">
                    <p class="fs-18 fw-bold">Discount Coupon (Optional)</p>

                    <div class="row g-3">
                        <div class="col-md-7">
                            <input type="text" id="couponCode" v-model="form.coupon" :readonly="couponChecked === true"
                                class="form-control rounded-pill px-4" placeholder="Enter coupon code" />
                        </div>
                        <div class="col-md-2 d-flex align-items-center">
                            <button type="button" class="btn rounded-pill px-4 fw-bold bg-orange text-white"
                                :disabled="couponChecked || coupanLoading" @click="coupnCheck">
                                <template v-if="coupanLoading">
                                    <i class="fa fa-spinner fa-spin"></i>
                                </template>
                                <template v-else>
                                    {{ couponChecked ? 'Applied' : 'Apply' }}
                                </template>
                        </button>
                        </div>
                        <div class="col-md-3">
                            <div class="d-flex">
                                <div v-if="couponChecked && coupanObject">
                                    <p v-if="coupanObject?.percent_off" class="mb-0"><b>Percent Off:</b> {{ coupanObject?.percent_off }}% <b>Duration:</b> {{ coupanObject?.duration }}</p>
                                    <p v-if="coupanObject?.amount_off" class="mb-0"><b>Amount Off:</b> ${{ coupanObject?.amount_off / 100 }} <b>Duration:</b> {{ coupanObject?.duration }}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <small v-if="couponMessage" class="text-danger mt-1 d-block">
                        {{ couponMessage }}
                    </small>
                </div>

                <div v-if="paymentRequired" class="col-12 mt-4">
                    <p class="fs-18 fw-bold mb-2">Payment Method</p>
                    <p class="fs-14 text-muted ">
                        Payment is required only if the final price is greater than $0.
                    </p>

                    <template v-if="!hasBillingAttached">
                        <StripePayment ref="stripePM" title="" />
                    </template>

                    <template v-else>
                        <div class="border rounded-3 p-3 mb-3 d-flex justify-content-between align-items-center">
                            <div class="d-flex align-items-center gap-3">
                                <div class="card-icon">
                                    <i class="fas fa-credit-card text-orange fs-20"></i>
                                </div>
                                <div>
                                    <p class="mb-0 fw-semibold">
                                        **** **** **** {{ user.pm_last_four }}
                                    </p>
                                    <small class="text-muted text-capitalize">
                                        {{ user.pm_type }} • Default payment method
                                    </small>
                                </div>
                            </div>

                            <span class="badge bg-light text-orange border border-orange">
                                Default
                            </span>
                        </div>

                        <div class="form-check mb-3">
                            <input class="form-check-input" type="checkbox" id="useNewPayment"
                                v-model="needNewBilling" />
                            <label class="form-check-label fw-semibold" for="useNewPayment">
                                Use a new payment method
                            </label>
                        </div>

                        <transition name="fade">
                            <div v-if="needNewBilling">
                                <StripePayment ref="stripePM" title="" />
                            </div>
                        </transition>
                    </template>
                </div>

                <div class="col-12 mb-4">
                    <div class="p-3 border rounded-3 d-flex justify-content-between align-items-center">
                        <div>
                            <p class="mb-1 fs-14 text-muted">Final Amount</p>
                            <h4 class="fw-bold mb-0">${{ finalPrice.toFixed(2) }}</h4>
                        </div>

                        <SubmitButton type="button" :processing="processing" @clicked="savePaymentMethod"
                            defaultClass="btn bg-orange text-white px-5 fw-bold rounded-pill"
                            text="Confirm & Switch Plan" />
                    </div>
                </div>

            </div>
        </b-modal>
    </div>
</template>

<script>
import StripePayment from "@/components/StripePaymentMethod";
import SubmitButton from "@/components/general/SubmitButton.vue";
import Swal from "sweetalert2";

export default {
    name: "SwitchPlanModal",
    props: {
        user: {
            type: Object,
            required: true
        }
    },
    components: {
        StripePayment,
        SubmitButton
    },
    data() {
        return {
            payment_method_id: null,
            processing: false,
            couponChecked: false,
            coupanLoading: false,
            couponError: false,
            couponMessage: '',
            paymentRequired: true,
            needNewBilling:false,
            coupanObject: null,
            form: {
                interval: 'month',
                coupon: null,
                payment_method_id: null,
                user_id: null,
                plan_name:'Premium'
            },
            pricing: {
                month: {
                    price: 9.00,
                    interval: 'month'
                },
                year: {
                    price: 99.00,
                    interval: 'year'
                }
            },
            couponAmount: 0,
        }
    },
    computed: {
        userCurrentPlanInterval() {
            let interval = this.user?.subscriptions[0]?.subscription_plan?.interval ?? null;
            if (interval && interval == 'month') {
                return 'Monthly';
            } else if (interval && interval == 'year') {
                return 'Yearly';
            }

            return interval;

        },
        isActiveSubscription() {
            const status = this.user?.subscriptions[0]?.stripe_status;
            return status === 'active' || status === 'trialing';
        },
        currentIntervalRaw() {
            return this.user?.subscriptions[0]?.subscription_plan?.interval;
        },
        userCurrentSubscriptionName() {
            return this.user?.subscriptions[0]?.name ?? 'N/A';
        },
        basePrice() {
            return this.pricing[this.form.interval].price;
        },
        finalPrice() {
            if (this.couponChecked) {
                return Math.max(this.basePrice - this.couponAmount, 0);
            }
            return this.basePrice;
        },
        hasBillingAttached() {
            return this.user.stripe_id && this.user.pm_type && this.user.pm_last_four;
        }
    },
    methods: {
        openModal() {
            const currentInterval = this.user?.subscriptions[0]?.subscription_plan?.interval;
            const status = this.user?.subscriptions[0]?.stripe_status;

            if (
                currentInterval &&
                (status === 'active' || status === 'trialing')
            ) {
                // Default to opposite interval
                this.form.interval = currentInterval === 'month' ? 'year' : 'month';
            } else {
                this.form.interval = 'month';
            }
            this.form.user_id = this.user.id;
            this.$refs.switchPlanModal.show();
        },
        closeModal() {
            this.$refs.switchPlanModal.hide();
        },
        async manageBillingType(type) {

            if (type == this.currentIntervalRaw && (this.user?.subscriptions[0]?.stripe_status == 'active' || this.user?.subscriptions[0]?.stripe_status == 'trialing')) {
                this.triggerSwal('Customer already has this subscriptoin', "error");
                return;
            }

            this.form.interval = type;
            if(this.form.coupon && this.couponChecked){
              await this.getCoupanAmountOff();
            }
            if (this.finalPrice == 0) {
                this.paymentRequired = false;
            } else {
                this.paymentRequired = true;
            }
        },
        async coupnCheck() {
            if (!this.form.coupon) {
                return true;
            }
            this.coupanLoading = true;
            await new Promise(resolve => setTimeout(resolve, 2000));
            await this.checkCoupn(this.form.coupon)
                .then((data) => {
                    this.couponError = data.status;
                    if (!this.couponError) {
                        this.processing = false;
                        this.coupanLoading = false;
                    }
                    if (data.status) {
                        this.coupanObject = data.coupon;
                        this.getCoupanAmountOff();
                        this.couponChecked = true;
                        this.triggerSwal('Coupon applied', "success");
                        this.paymentRequired = this.finalPrice > 0;
                        this.coupanLoading = false;
                    } else {
                        this.couponMessage = data.message;
                        this.paymentRequired = true;
                        this.coupanLoading = false;
                        this.couponAmount = 0;
                        this.paymentRequired = true;
                    }
                    return true;
                });


            if (!this.couponError) {
                this.form.processing = false;
                this.coupanLoading = false;
                return false;
            }

        },
        getCoupanAmountOff() {
            this.couponAmount = 0;

            let coupon = this.coupanObject;

            if (!coupon) {
                return;
            }

            const price = this.pricing[this.form.interval].price;

            if (coupon.amount_off) {
                this.couponAmount = coupon.amount_off / 100;
            }

            if (coupon.percent_off) {
                this.couponAmount = (price * coupon.percent_off) / 100;
            }

            return this.couponAmount;
        },
        async savePaymentMethod() {
            this.processing = true;
            if (!this.hasBillingAttached || this.needNewBilling) {
                var payment_method_id = await this.$refs.stripePM.getPaymentMethodId();
                if (!payment_method_id && this.needNewBilling) {
                    this.triggerSwal("Please Complete Payment Details", "error");
                    this.processing = false;
                    return;
                }else {
                    this.form.payment_method_id = payment_method_id;
                }
            }
            try {
                Swal.fire({
                    title: "Are you sure you want to switch plan?",
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonColor: "#3085d6",
                    cancelButtonColor: "#d33",
                    confirmButtonText: "Yes, Switch it!",
                }).then(async (result) => {
                    if (result.isConfirmed) {
                        let url = 'subscriptions/switch-subscription';
                        this.$axios.post(url, this.form).then((response) => {
                            this.user = null;
                            this.$emit('plan-switched');
                            this.triggerSwal("Subscription Plan Switched Successfully", "success");
                            this.closeModal();
                        }).catch((error) => {
                            this.handleErrorResponse(error.response, "error");
                        }).finally(() => {
                            this.processing = false;
                        });

                    }else {
                        this.processing = false;
                    }

                });
            } catch (error) {
                this.handleErrorResponse(error.response, "error");
                this.processing = false;
            }

        },
    }

}
</script>

<style scoped>
    
.current-plan-badge {
  position: absolute;
  top: 8px;
  left: 50%;
  transform: translateX(-50%);
  background: #ffffff;
  color: #f54b1e;
  border: 1px solid #f54b1e;
  padding: 2px 10px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 999px;
  white-space: nowrap;
}

.billing-card.disabled {
  background: #fafafa;
  border-style: dashed;
  cursor: not-allowed;
}

.billing-card.active {
  background: #f54b1e;
  border-color: #f54b1e;
  color: #ffffff;
}

.billing-card.active h5,
.billing-card.active h6,
.billing-card.active p {
  color: #ffffff !important;
}
.card-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #f54b1e;
  color:#ffffff !important;
  display: flex;
  align-items: center;
  justify-content: center;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>